import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useComponentWebSocket, useGlobalSettingState } from './GlobalSettingUtils';
import PluginEditorDialog from './PluginEditorDialog';
import PluginImportButton from '../PluginImportButton';
import { apiBinaryRequest, apiRequest } from '../../Utils/apiClient';

const PluginSettings = forwardRef((_props, ref) => {
  const { t, openSnackbar } = useGlobalSettingState();
  useImperativeHandle(ref, () => ({}));

  const [plugins, setPlugins] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [reloading, setReloading] = useState(false);

  const [selectedFolder, setSelectedFolder] = useState('');
  const [pluginName, setPluginName] = useState('');
  const [pluginType, setPluginType] = useState('');
  const [pluginKind, setPluginKind] = useState('');
  const [manifestFile, setManifestFile] = useState('plugin.json');
  const [scriptFile, setScriptFile] = useState('plugin.py');

  const [pluginJson, setPluginJson] = useState('');
  const [pluginPy, setPluginPy] = useState('');
  const [memoryJson, setMemoryJson] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [memoryDialogOpen, setMemoryDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const applyPluginFilesPayload = useCallback((data) => {
    const plugin = data.plugin || {};
    setSelectedFolder(plugin.folderName || '');
    setPluginName(plugin.name || plugin.folderName || '');
    setPluginType(plugin.type || '');
    setPluginKind(plugin.kind || '');
    setManifestFile(plugin.manifestFile || 'plugin.json');
    setScriptFile(plugin.scriptFile || 'plugin.py');
    setPluginJson(data.pluginJson || '');
    setPluginPy(data.pluginPy || '');
    setMemoryJson(data.memoryJson || '');
    setHasChanges(false);
  }, []);

  const loadPluginFiles = useCallback(async (folderName) => {
    const normalizedFolder = String(folderName || '').trim();
    if (!normalizedFolder) {
      return;
    }

    setLoadingFiles(true);
    try {
      const data = await apiRequest(`/api/v1/settings/plugins/${encodeURIComponent(normalizedFolder)}/files`);
      applyPluginFilesPayload(data);
    } catch (error) {
      openSnackbar(
        error?.message || t('global_setting.plugins_files_load_error', 'Error loading plugin files'),
        3500
      );
    } finally {
      setLoadingFiles(false);
    }
  }, [apiRequest, applyPluginFilesPayload, openSnackbar, t]);

  const loadPlugins = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await apiRequest('/api/v1/settings/plugins');
      const incomingPlugins = Array.isArray(data.plugins) ? data.plugins : [];
      setPlugins(incomingPlugins);

      if (!incomingPlugins.length) {
        setSelectedFolder('');
        setPluginName('');
        setPluginType('');
        setPluginKind('');
        setPluginJson('');
        setPluginPy('');
        setMemoryJson('');
        setHasChanges(false);
        return;
      }

      const hasSelected = incomingPlugins.some((plugin) => plugin.folderName === selectedFolder);
      const nextFolder = hasSelected ? selectedFolder : incomingPlugins[0].folderName;
      if (nextFolder) {
        if (nextFolder !== selectedFolder) {
          setSelectedFolder(nextFolder);
        }
        await loadPluginFiles(nextFolder);
      }
    } catch (error) {
      openSnackbar(
        error?.message || t('global_setting.plugins_load_error', 'Error loading plugins'),
        3500
      );
    } finally {
      setLoadingList(false);
    }
  }, [apiRequest, loadPluginFiles, openSnackbar, selectedFolder, t]);

  const commandHandlers = useMemo(
    () => ({
      savePluginFilesForSettings: (message) => {
        const data = message.data || message;
        setSaving(false);
        if (data.status !== 'success') {
          openSnackbar(
            data.message || t('global_setting.plugins_save_error', 'Error saving plugin'),
            3500
          );
          return;
        }

        openSnackbar(t('global_setting.plugins_save_success', 'Plugin saved successfully'), 2500);
        const plugin = data.plugin || {};
        setManifestFile(plugin.manifestFile || manifestFile);
        setScriptFile(plugin.scriptFile || scriptFile);
        setPluginJson(data.pluginJson || pluginJson);
        setPluginPy(data.pluginPy || pluginPy);
        setMemoryJson(data.memoryJson || memoryJson);
        setHasChanges(false);

        loadPlugins();
      },
      deletePluginForSettings: (message) => {
        const data = message.data || message;
        setDeleting(false);
        setDeleteDialogOpen(false);
        if (data.status !== 'success') {
          openSnackbar(
            data.message || t('global_setting.plugins_delete_error', 'Error deleting plugin'),
            3500
          );
          return;
        }

        openSnackbar(t('global_setting.plugins_delete_success', 'Plugin deleted successfully'), 2500);
        setEditDialogOpen(false);
        setMemoryDialogOpen(false);
        setSelectedFolder('');
        setPluginName('');
        setPluginType('');
        setPluginKind('');
        setPluginJson('');
        setPluginPy('');
        setMemoryJson('');
        setHasChanges(false);
        loadPlugins();
      },
      reloadPluginsForSettings: (message) => {
        const data = message.data || message;
        setReloading(false);
        if (data.status !== 'success') {
          openSnackbar(
            data.message || t('global_setting.plugins_reload_error', 'Error reloading plugins'),
            3500
          );
          return;
        }

        openSnackbar(
          t('global_setting.plugins_reload_success', 'Plugins reloaded successfully'),
          2500
        );
        loadPlugins();
      }
    }),
    [
      t,
      openSnackbar,
      manifestFile,
      scriptFile,
      pluginJson,
      pluginPy,
      memoryJson,
      loadPlugins
    ]
  );

  const { sendCommand } = useComponentWebSocket(commandHandlers);

  const getPluginKindMeta = useCallback((plugin) => {
    const normalizedKind = String(plugin?.kind || '').trim().toLowerCase();
    if (normalizedKind === 'connector') {
      return {
        label: t('global_setting.plugins_kind_connector', 'Data Connector'),
        color: 'primary'
      };
    }
    if (normalizedKind) {
      return {
        label: t('global_setting.plugins_kind_action', 'Action'),
        color: 'success'
      };
    }
    return {
      label: t('global_setting.plugins_kind_unknown', 'Unknown'),
      color: 'default'
    };
  }, [t]);

  const isAiGeneratedPlugin = useCallback((plugin) => {
    const typeValue = String(plugin?.type || '').trim();
    const nameValue = String(plugin?.name || '').trim();
    const descriptionValue = String(plugin?.description || '').trim();
    return plugin?.isAIGenerated === true
      || plugin?.hasMemory === true
      || /^auto_/i.test(typeValue)
      || /^auto_/i.test(nameValue)
      || /_auto_/i.test(typeValue)
      || /generated by ai/i.test(descriptionValue);
  }, []);

  useEffect(() => {
    loadPlugins();
  }, [loadPlugins]);

  const handleSave = useCallback(() => {
    if (!selectedFolder) {
      return;
    }

    try {
      JSON.parse(pluginJson || '{}');
    } catch {
      openSnackbar(t('global_setting.plugins_invalid_json', 'Plugin JSON is invalid'), 3500);
      return;
    }

    setSaving(true);
    sendCommand('savePluginFilesForSettings', {
      folderName: selectedFolder,
      manifestFile,
      pluginJson,
      pluginPy
    });
  }, [selectedFolder, manifestFile, pluginJson, pluginPy, openSnackbar, sendCommand, t]);

  const handleDeletePlugin = useCallback(() => {
    if (!selectedFolder) {
      return;
    }
    setDeleting(true);
    sendCommand('deletePluginForSettings', {
      folderName: selectedFolder
    });
  }, [selectedFolder, sendCommand]);

  const handleExportPlugin = useCallback(async () => {
    if (!selectedFolder) {
      return;
    }
    setExporting(true);
    try {
      const response = await apiBinaryRequest(
        `/api/v1/settings/plugins/${encodeURIComponent(selectedFolder)}/archive`
      );
      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition') || '';
      const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || `${selectedFolder}.zip`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      openSnackbar(t('global_setting.plugins_export_success', 'Plugin exported as ZIP'), 2500);
    } catch (error) {
      openSnackbar(
        error?.message || t('global_setting.plugins_export_error', 'Error exporting plugin'),
        3500
      );
    } finally {
      setExporting(false);
    }
  }, [apiBinaryRequest, openSnackbar, selectedFolder, t]);

  const handleImportComplete = useCallback(async (data) => {
    setImporting(true);
    try {
      const nextFolder = String(data?.folderName || data?.folder_name || '').trim();
      await loadPlugins();
      if (nextFolder) {
        await loadPluginFiles(nextFolder);
      }
    } finally {
      setImporting(false);
    }
  }, [loadPluginFiles, loadPlugins]);

  const handleReloadPlugins = useCallback(() => {
    setReloading(true);
    sendCommand('reloadPluginsForSettings');
  }, [sendCommand]);

  const hasMemory = Boolean(memoryJson && String(memoryJson).trim());
  const openPluginEditor = useCallback(
    (folderName) => {
      if (!folderName) {
        return;
      }
      if (hasChanges && selectedFolder && selectedFolder !== folderName) {
        const shouldContinue = window.confirm(
          t('global_setting.plugins_unsaved_confirm', 'You have unsaved changes. Discard them?')
        );
        if (!shouldContinue) {
          return;
        }
      }
      setEditDialogOpen(true);
      setSelectedFolder(folderName);
      loadPluginFiles(folderName);
    },
    [hasChanges, selectedFolder, loadPluginFiles, t]
  );

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t(
          'global_setting.plugins_description',
          'Read plugin memory and edit plugin.json / python code directly for auto_* plugins only.'
        )}
      </Alert>

      <Stack direction="column" spacing={2}>
        <Paper variant="outlined" sx={{ width: '100%' }}>
          <Box sx={{ p: 1.5, borderBottom: '1px solid #e5e7eb' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t('global_setting.plugins_section', 'Plugins')}
              </Typography>
              <Stack direction="row" spacing={1}>
                <PluginImportButton
                  disabled={importing || loadingList}
                  onImportComplete={handleImportComplete}
                  openSnackbar={openSnackbar}
                />
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={loadPlugins}
                  disabled={loadingList || importing}
                >
                  {t('global_setting.refresh', 'Refresh')}
                </Button>
              </Stack>
            </Stack>
          </Box>
          {loadingList ? (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <List dense disablePadding>
              {plugins.map((plugin) => {
                const kindMeta = getPluginKindMeta(plugin);
                const aiGenerated = isAiGeneratedPlugin(plugin);
                return (
                  <ListItem
                    key={plugin.folderName}
                    secondaryAction={(
                      <Tooltip title={t('global_setting.plugins_open_editor', 'Open editor')}>
                        <IconButton edge="end" onClick={() => openPluginEditor(plugin.folderName)}>
                          <MoreHorizIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  >
                    <ListItemText
                      primary={(
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {plugin.name || plugin.folderName}
                          </Typography>
                          {aiGenerated && (
                            <Tooltip title={t('generic_connector.ai_generated_badge', 'AI-generated plugin')} arrow>
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '50%',
                                  color: '#ffffff',
                                  background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                                  boxShadow: '0 4px 10px rgba(37, 99, 235, 0.35)',
                                }}
                              >
                                <AutoAwesomeIcon sx={{ fontSize: 12 }} />
                              </Box>
                            </Tooltip>
                          )}
                          <Chip
                            size="small"
                            label={kindMeta.label}
                            color={kindMeta.color}
                            variant="outlined"
                          />
                        </Stack>
                      )}
                      secondary={`${plugin.folderName} - ${plugin.type || t('global_setting.plugins_kind_unknown', 'Unknown')}`}
                    />
                  </ListItem>
                );
              })}
              {!plugins.length && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('global_setting.plugins_no_plugins', 'No plugins found')}
                  </Typography>
                </Box>
              )}
            </List>
          )}
        </Paper>

      </Stack>

      <PluginEditorDialog
        open={editDialogOpen}
        onClose={() => {
          if (!saving && !deleting) {
            setEditDialogOpen(false);
          }
        }}
        t={t}
        selectedFolder={selectedFolder}
        pluginKind={pluginKind}
        pluginType={pluginType}
        manifestFile={manifestFile}
        scriptFile={scriptFile}
        loadingFiles={loadingFiles}
        pluginJson={pluginJson}
        pluginPy={pluginPy}
        onPluginJsonChange={(value) => {
          setPluginJson(value);
          setHasChanges(true);
        }}
        onPluginPyChange={(value) => {
          setPluginPy(value);
          setHasChanges(true);
        }}
        hasMemory={hasMemory}
        deleting={deleting}
        saving={saving}
        exporting={exporting}
        importing={importing}
        reloading={reloading}
        onOpenMemory={() => setMemoryDialogOpen(true)}
        onOpenDelete={() => setDeleteDialogOpen(true)}
        onExport={handleExportPlugin}
        onReload={handleReloadPlugins}
        onSave={handleSave}
      />

      <Dialog
        open={memoryDialogOpen}
        onClose={() => setMemoryDialogOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>{t('global_setting.plugins_memory_dialog_title', 'Plugin memory')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('global_setting.plugins_memory_label', 'memory.json (read-only)')}
            value={memoryJson || ''}
            multiline
            minRows={18}
            fullWidth
            InputProps={{
              readOnly: true,
              sx: {
                fontFamily: 'monospace',
                fontSize: '0.82rem',
                backgroundColor: '#fafafa'
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemoryDialogOpen(false)}>
            {t('global_setting.cancel', 'Cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!deleting) {
            setDeleteDialogOpen(false);
          }
        }}
      >
        <DialogTitle>{t('global_setting.plugins_delete_confirm_title', 'Delete plugin')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              'global_setting.plugins_delete_confirm_message',
              'Are you sure you want to delete plugin "{{name}}"?',
              { name: pluginName || selectedFolder }
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={deleting} onClick={() => setDeleteDialogOpen(false)}>
            {t('global_setting.cancel', 'Cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={handleDeletePlugin}
          >
            {deleting
              ? t('global_setting.plugins_deleting', 'Deleting...')
              : t('global_setting.plugins_delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export { PluginSettings };
